// markup/edit.mjs — DropPin plan-markup EDIT geometry (pure, headless).
// ---------------------------------------------------------------------------
// The reshape / resize transforms, marquee (rubber-band) intersection, and the
// snapping + ortho-constrain maths that make the drawing feel professional.
// NO React / DOM — every export is pure and node-testable, and every distance /
// bbox number is the shared engine's (@insitebg/plan-markup-core), never re-derived.
//
// COORDINATE MODEL — points are NORMALISED (0..1) like the rest of the model.
// Angle + distance work happens in PIXEL space (isotropic — the viewer scales x
// and y by one factor, normalised space does not), so the snap helpers take the
// drawing's natural W,H and convert internally, exactly like measure.mjs.
// ---------------------------------------------------------------------------

import { distance } from '@insitebg/plan-markup-core';
import { boundsOf } from './model.mjs';
import { legendBoxNorm } from './legend.mjs';

// ---- Rectangle resize (corner + edge handles) -----------------------------

// Handle ids in a stable render order (4 corners + 4 edge midpoints).
export const RECT_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// CSS cursor for a rect handle so the affordance reads correctly on hover.
export function rectHandleCursor(id) {
  switch (id) {
    case 'nw': case 'se': return 'nwse-resize';
    case 'ne': case 'sw': return 'nesw-resize';
    case 'n': case 's': return 'ns-resize';
    case 'e': case 'w': return 'ew-resize';
    default: return 'move';
  }
}

// Normalised position of a rect handle, from a bbox {minX,minY,maxX,maxY}.
export function rectHandlePoint(b, id) {
  const cx = (b.minX + b.maxX) / 2, cy = (b.minY + b.maxY) / 2;
  switch (id) {
    case 'nw': return { x: b.minX, y: b.minY };
    case 'n': return { x: cx, y: b.minY };
    case 'ne': return { x: b.maxX, y: b.minY };
    case 'e': return { x: b.maxX, y: cy };
    case 'se': return { x: b.maxX, y: b.maxY };
    case 's': return { x: cx, y: b.maxY };
    case 'sw': return { x: b.minX, y: b.maxY };
    case 'w': return { x: b.minX, y: cy };
    default: return { x: cx, y: cy };
  }
}

// Apply a rect-handle drag: move ONLY the edge(s) that handle controls to the
// pointer (nx,ny); the opposite edge(s) stay put. Returns a new bbox. Corners
// may cross over (min>max) mid-drag — rectXYWH already normalises via min/max,
// so the live preview and the committed shape both read correctly.
export function applyRectHandle(b, id, nx, ny) {
  let { minX, minY, maxX, maxY } = b;
  if (id.includes('w')) minX = nx;
  if (id.includes('e')) maxX = nx;
  if (id.includes('n')) minY = ny;
  if (id.includes('s')) maxY = ny;
  return { minX, minY, maxX, maxY };
}

// bbox {minX,minY,maxX,maxY} -> the 2-corner point list a rect object stores.
export function bboxToRectPoints(b) {
  return [{ x: b.minX, y: b.minY }, { x: b.maxX, y: b.maxY }];
}

// ---- Per-vertex reshape ----------------------------------------------------

// Move vertex `index` of a point list to (nx,ny). Returns a NEW point list
// (immutable — the source is never mutated).
export function moveVertex(points, index, nx, ny) {
  return (points || []).map((p, i) => (i === index ? { x: nx, y: ny } : { x: p.x, y: p.y }));
}

// ---- Text box resize (font scaling) ---------------------------------------

// Estimated normalised box for a text object (anchor = top-left, y-down),
// matching the hit-test's width model (fontSize * 0.6 * chars). Used for the
// selection outline, the resize handle position, and marquee coverage.
export function textBoxNorm(o, W, H) {
  const a = (o.points && o.points[0]) || { x: 0, y: 0 };
  const fs = (o.style && o.style.fontSize) || 15;
  const wPx = Math.max(fs, ((o.text ? o.text.length : 1) * fs * 0.6));
  return { minX: a.x, minY: a.y, maxX: a.x + wPx / W, maxY: a.y + fs / H };
}

// New font size (screen/natural px) when a text box's bottom edge is dragged to
// `pointerNorm`: the box height in pixels from the anchor, clamped. Drag down
// grows the type, up shrinks it.
export function fontSizeForDrag(anchorNorm, pointerNorm, H, min = 8, max = 240) {
  const px = (pointerNorm.y - anchorNorm.y) * H;
  return Math.max(min, Math.min(max, px));
}

// ---- Marquee (rubber-band) multi-select ------------------------------------

// Normalised rect {minX,minY,maxX,maxY} from two corner points.
export function rectFromCorners(a, b) {
  return {
    minX: Math.min(a.x, b.x), minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x), maxY: Math.max(a.y, b.y),
  };
}

// Axis-aligned bounding-box overlap (edges touching counts as intersecting).
export function rectsIntersect(r1, r2) {
  return r1.minX <= r2.maxX && r1.maxX >= r2.minX && r1.minY <= r2.maxY && r1.maxY >= r2.minY;
}

// Ids of every object whose bounding box intersects the marquee rect. Bounds
// come from the model's boundsOf (which uses the shared core's bbox). Text uses
// its measured box so a marquee that only grazes the glyphs still selects it.
export function marqueeSelect(objects, rectNorm, W, H) {
  const ids = [];
  for (const o of objects || []) {
    const b = o.type === 'text' ? textBoxNorm(o, W || 1, H || 1)
      : o.type === 'legend' ? legendBoxNorm(o, W || 1, H || 1)
      : boundsOf(o, 0);
    if (b && rectsIntersect(b, rectNorm)) ids.push(String(o.id));
  }
  return ids;
}

// ---- Snapping + ortho-constrain --------------------------------------------

// Ortho / angle-constrain: snap the segment anchor->p to the nearest multiple
// of `stepDeg` (default 45°). The angle + length are taken in PIXEL space, then
// the endpoint is written back NORMALISED at the same pixel length — so a "45°"
// line is 45° on screen, not in the anisotropic normalised space.
export function snapAngle(anchorNorm, pNorm, W, H, stepDeg = 45) {
  const ax = anchorNorm.x * W, ay = anchorNorm.y * H;
  const px = pNorm.x * W, py = pNorm.y * H;
  const len = distance({ x: ax, y: ay }, { x: px, y: py });
  if (len === 0) return { x: pNorm.x, y: pNorm.y };
  const step = (stepDeg * Math.PI) / 180;
  const ang = Math.round(Math.atan2(py - ay, px - ax) / step) * step;
  return { x: (ax + Math.cos(ang) * len) / W, y: (ay + Math.sin(ang) * len) / H };
}

// All vertices (normalised) usable as snap targets, excluding one object id
// (so a vertex being reshaped never snaps to itself).
export function collectSnapPoints(objects, excludeId = null) {
  const out = [];
  for (const o of objects || []) {
    if (excludeId != null && String(o.id) === String(excludeId)) continue;
    if (o.type === 'legend') continue; // a stamp's corner is not a point on the plan
    for (const p of o.points || []) out.push({ x: p.x, y: p.y });
  }
  return out;
}

// Snap `pNorm` to the nearest candidate within `tolPx` PIXELS (via the core's
// distance). Returns the snapped candidate, or the original point if none near.
export function snapToPoints(pNorm, candidates, tolPx, W, H) {
  const p = { x: pNorm.x * W, y: pNorm.y * H };
  let best = null, bestD = tolPx;
  for (const c of candidates || []) {
    const d = distance(p, { x: c.x * W, y: c.y * H });
    if (d <= bestD) { bestD = d; best = c; }
  }
  return best ? { x: best.x, y: best.y } : { x: pNorm.x, y: pNorm.y };
}

// Snap to a light pixel-square grid (`stepPx` natural pixels). Returns a new
// point on the nearest grid intersection.
export function snapToGrid(pNorm, stepPx, W, H) {
  const s = Math.max(1, stepPx || 1);
  return {
    x: (Math.round((pNorm.x * W) / s) * s) / W,
    y: (Math.round((pNorm.y * H) / s) * s) / H,
  };
}

// Resolve the effective point for a NEW or EDITED vertex given the active aids.
// Priority (predictable, testable): explicit ORTHO (Shift, needs an anchor)
// wins so holding Shift always gives a clean angle; else snap to an existing
// VERTEX if one is within tolerance; else optional GRID. Freehand passes all
// aids off so it is never fought. Returns a normalised point.
export function resolvePoint(pNorm, opts = {}) {
  const { anchor, ortho, snapPts, tolPx = 8, grid, gridPx = 24, W = 1, H = 1 } = opts;
  if (ortho && anchor) return snapAngle(anchor, pNorm, W, H);
  if (snapPts && snapPts.length) {
    const s = snapToPoints(pNorm, snapPts, tolPx, W, H);
    if (s.x !== pNorm.x || s.y !== pNorm.y) return s;
  }
  if (grid) return snapToGrid(pNorm, gridPx, W, H);
  return { x: pNorm.x, y: pNorm.y };
}

// ---- Taking a node (2026-09-15) --------------------------------------------
// Brett: "need to be able to select a node and relocate it". A press on a corner
// dot of an unselected area dragged the whole area; only a selected shape's
// handles moved one corner. These are the shapes whose corners are nodes — the
// same set the select tool gives vertex handles (a rectangle resizes by its box,
// text and a legend by a corner, freehand only moves).
export const NODE_TYPES = ['line', 'arrow', 'polyline', 'polygon', 'measure'];

// The node under `pPx` (natural px) within `tolPx`: { id, index } or null.
// Shapes in `preferIds` (the selection) are searched first, then the rest from
// the top down — so on a corner two areas share, the selected one is taken, else
// the one drawn last. Within a shape, the nearest node wins.
export function nodeAt(objects, pPx, tolPx, W, H, preferIds = []) {
  const prefer = new Set((preferIds || []).map(String));
  const list = Array.isArray(objects) ? objects : [];
  const order = [
    ...list.filter((o) => o && prefer.has(String(o.id))).reverse(),
    ...list.filter((o) => o && !prefer.has(String(o.id))).reverse(),
  ];
  for (const o of order) {
    if (!NODE_TYPES.includes(o.type) || !Array.isArray(o.points)) continue;
    let best = -1;
    let bestD = tolPx;
    o.points.forEach((p, i) => {
      const d = distance(pPx, { x: p.x * W, y: p.y * H });
      if (d <= bestD) { bestD = d; best = i; }
    });
    if (best >= 0) return { id: o.id, index: best };
  }
  return null;
}
